import { useCallback } from 'react';
import { useAuthenticatedApiCall } from './api.service';
import { api } from '../config/api';

const baseConfig = {
  baseURL: api.API_URL,
};

export interface ApiToken {
  id: string;
  createdAt: string;
  expiresAt: string;
}

export interface ApiUser {
  id: string;
  name: string;
  email: string;
  tokens: ApiToken[];
}

export interface GetActiveTokensResponse {
  users: ApiUser[];
}

export function useApiAccessService() {
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);

  const generateToken = useCallback(async () => {
    const { data, error } = await makeAuthenticatedApiCall<{
      redirectUrl: string;
    }>({
      url: '/analytics-api-access/authorize',
      method: 'GET',
    });

    return { data, error };
  }, [makeAuthenticatedApiCall]);

  const exchangeCodeForToken = useCallback(
    async (code: string) => {
      const { data, error } = await makeAuthenticatedApiCall<{
        refreshToken: string;
      }>({
        url: '/analytics-api-access/exchange',
        method: 'POST',
        data: { code },
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  const getActiveTokens = useCallback(async () => {
    const { data, error } = await makeAuthenticatedApiCall<GetActiveTokensResponse>({
      url: '/analytics-api-access/tenant/refresh-tokens',
      method: 'GET',
    });
    return { data, error };
  }, [makeAuthenticatedApiCall]);

  const getTokensForUser = useCallback(
    async (userId: string) => {
      const { data, error } = await makeAuthenticatedApiCall<GetActiveTokensResponse>({
        url: '/analytics-api-access/tenant/refresh-tokens',
        method: 'GET',
      });
      if (error) {
        return { data: null, error };
      }
      const user = data?.users.find(u => u.id === userId);
      if (user) {
        return { data: { user, tokens: user.tokens }, error: null };
      }
      return { data: null, error: { message: 'User not found' } };
    },
    [makeAuthenticatedApiCall]
  );

  const revokeToken = useCallback(
    async (tokenId: string) => {
      const { data, error } = await makeAuthenticatedApiCall({
        url: `/analytics-api-access/tenant/user/refresh-token`,
        method: 'DELETE',
        params: { refreshTokenId: tokenId },
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  const revokeAllTokens = useCallback(
    async (userId: string) => {
      const { data, error } = await makeAuthenticatedApiCall({
        url: '/analytics-api-access/tenant/user/refresh-tokens',
        method: 'DELETE',
        params: { userId },
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  return {
    generateToken,
    exchangeCodeForToken,
    getActiveTokens,
    getTokensForUser,
    revokeToken,
    revokeAllTokens,
  };
}
